<ng-container *transloco="let t">
  <div class="card shadow flex-grow-1" style="min-height: 0">
    <div class="card-header border-0">
      <span class="h1 mb-0">
        {{ t('weather.weather.weatherAlertThresholdConfiguration') }}
      </span>
    </div>

    <div class="card-body pt-0">
      <div class="d-flex flex-column h-100" style="gap: 2rem">
        @switch (initializationState()) {
          @case ('loading') {
            <div class="flex-grow-1 center-single-child">
              <div class="text-center">
                <div class="spinner-border"></div>
                <div class="h3 text-primary mt-3 mb-0">
                  {{ t('weather.weather.loading') }}
                </div>
              </div>
            </div>
          }

          @case ('failed') {
            <div class="alert alert-danger align-items-center" role="alert">
              <i
                class="modus-icons alert-icon notranslate me-1"
                aria-hidden="true"
                >warning_outlined</i
              >
              <span>{{ t('weather.weather.failedToInitialize') }}</span>
              <button
                class="btn btn-sm btn-outline-danger"
                (click)="loadSettings()"
              >
                {{ t('weather.weather.reload') }}
              </button>
            </div>
          }

          @case ('initialized') {
            <mop-message class="mb-0 gap-3">
              <div class="d-flex">
                @if (canEditWeather) {
                  <span>
                    {{ t('weather.weather.infoMessage') }}
                  </span>
                } @else {
                  <span>
                    {{ t('weather.weather.infoMessageReadOnly') }}
                  </span>
                }
              </div>

              <button
                type="button"
                class="btn btn-outline-primary btn-sm text-nowrap ms-auto"
                (click)="openSupportArticle()"
              >
                {{ t('weather.weather.learnMore') }}
              </button>
            </mop-message>

            <div
              class="flex-grow-1"
              style="
                min-height: 0;
                overflow-y: auto;
                container-type: inline-size;
              "
            >
              <form class="container-grid" [formGroup]="weatherAlertsForm">
                <div class="configs-grid">
                  <div class="d-flex flex-column flex-grow-1 gap-3">
                    <div class="d-flex align-items-center">
                      <label
                        class="form-label fw-bold m-0"
                        for="roadRiskThreshold"
                      >
                        {{ t('weather.weather.roadRiskThreshold') }}
                      </label>

                      <button
                        type="button"
                        class="btn text-tertiary btn-icon-only shadow-none border-0"
                        [tooltip]="t('weather.weather.aiTooltip')"
                        triggers="focus"
                        placement="top"
                        [adaptivePosition]="false"
                      >
                        <img
                          loading="lazy"
                          alt="Powered by AI"
                          height="18"
                          width="22"
                          src="/assets/img/ai-stars.svg"
                          style="margin-top: -8px; cursor: pointer"
                        />
                      </button>

                      <tmx-inline-help
                        [summary]="t('weather.weather.roadRiskThreshold')"
                        [description]="
                          t(
                            'weather.weather.roadRiskThresholdInlineHelpDescription'
                          )
                        "
                      ></tmx-inline-help>
                    </div>

                    <div class="px-3">
                      <div class="d-flex justify-content-between">
                        <span class="h6 text-uppercase text-tertiary">
                          {{ t('weather.weather.noRisk') }}</span
                        >
                        <span class="h6 text-uppercase text-tertiary">{{
                          t('weather.weather.moderate')
                        }}</span>
                        <span class="h6 text-uppercase text-tertiary">
                          {{ t('weather.weather.high') }}
                        </span>
                      </div>
                      <tmx-slider
                        id="roadRiskThreshold"
                        [min]="0"
                        [max]="roadRiskOptions().length - 1"
                        [formControl]="roadRiskThresholdControl"
                      ></tmx-slider>
                      <tmx-meter
                        [min]="0"
                        [max]="roadRiskOptions().length - 1"
                      ></tmx-meter>
                    </div>

                    @if (
                      roadRiskOptions()[roadRiskThresholdControl.getRawValue()];
                      as selectedRoadRisk
                    ) {
                      <div>
                        <strong
                          >({{ selectedRoadRisk.value
                          }}{{
                            selectedRoadRisk.value === 4
                              ? t('weather.weather.srrDefault')
                              : ''
                          }}) {{ selectedRoadRisk.label }}</strong
                        >
                        <div>{{ selectedRoadRisk.description }}</div>
                      </div>
                    }

                    <div
                      class="summary alert-warning rounded p-2 mt-3"
                      [class.pulse]="pulseSummary()"
                    >
                      <div>
                        <strong>{{ t('weather.weather.summaryIntro') }}</strong>
                      </div>
                      <ul>
                        @if (
                          roadRiskOptions()[
                            roadRiskThresholdControl.getRawValue()
                          ];
                          as selectedRoadRisk
                        ) {
                          <li>
                            {{ t('weather.weather.roadRiskThresholdSummary') }}
                            <strong>{{
                              t('weather.weather.orHigherRoadRiskThreshold', {
                                risk: selectedRoadRisk.value,
                              })
                            }}</strong>
                          </li>
                        }

                        @if (enableMinWindSpeed.getRawValue()) {
                          <li>
                            {{ t('weather.weather.minWindSpeedSummary') }}
                            <strong>{{
                              t('weather.weather.aboveMinWindSpeed', {
                                min: minWindSpeedControl.getRawValue(),
                              })
                            }}</strong>
                          </li>
                        }

                        @if (enableMinWindGust.getRawValue()) {
                          <li>
                            {{ t('weather.weather.minWindGustSummary') }}
                            <strong>{{
                              t('weather.weather.aboveMinWindGust', {
                                min: minWindGustControl.getRawValue(),
                              })
                            }}</strong>
                          </li>
                        }

                        @if (enableTempThresholdControl.getRawValue()) {
                          <li>
                            {{
                              t('weather.weather.temperatureThresholdSummary')
                            }}
                            <strong>{{
                              t('weather.weather.minTempDescription', {
                                min: temperatureThresholdControl.getRawValue()
                                  .min,
                              })
                            }}</strong>
                            <span> {{ t('weather.weather.or') }} </span>
                            <strong>{{
                              t('weather.weather.maxTempDescription', {
                                max: temperatureThresholdControl.getRawValue()
                                  .max,
                              })
                            }}</strong>
                          </li>
                        }

                        @if (enableTippingIndexControl.getRawValue()) {
                          @if (
                            tippingIndexOptions()[
                              tippingIndexThresholdControl.getRawValue()
                            ];
                            as selectedTippingIndex
                          ) {
                            <li>
                              {{
                                t(
                                  'weather.weather.tippingIndexThresholdSummary'
                                )
                              }}
                              <strong>{{
                                t(
                                  'weather.weather.orHigherTippingIndexThreshold',
                                  {
                                    threshold: selectedTippingIndex.value,
                                  }
                                )
                              }}</strong>
                            </li>
                          }
                        }

                        @if (enableMinVisibility.getRawValue()) {
                          <li>
                            {{ t('weather.weather.minVisibilitySummary') }}
                            <strong>{{
                              t('weather.weather.aboveMinVisibility', {
                                min: minVisibilityControl.getRawValue(),
                              })
                            }}</strong>
                          </li>
                        }
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <div
                    class="d-flex justify-content-between align-items-center py-2 mb-5"
                  >
                    <span class="h2 m-0">{{
                      t('weather.weather.weatherAlertOverrides')
                    }}</span>
                    <span class="d-flex gap-3">
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        (click)="clearOverrides()"
                        [disabled]="!canEditWeather || disableClearOverrides()"
                      >
                        {{ t('weather.weather.clearOverrides') }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-primary"
                        [disabled]="
                          saving() ||
                          formUnchanged() ||
                          weatherAlertsForm.invalid
                        "
                        (click)="save()"
                      >
                        @if (saving()) {
                          <span class="spinner-border"></span>
                        }
                        <span>
                          {{
                            t('weather.weather.saveButton', {
                              saving: saving(),
                            })
                          }}
                        </span>
                      </button>
                    </span>
                  </div>

                  <div class="override-configs-grid">
                    <div class="config">
                      <div class="d-flex align-items-center gap-2">
                        <div class="form-check form-switch ms-1">
                          <input
                            id="enableMinWindSpeed"
                            type="checkbox"
                            role="switch"
                            class="form-check-input"
                            [formControlName]="'enableMinWindSpeed'"
                          />
                          <label
                            for="enableMinWindSpeed"
                            class="form-check-label"
                          >
                            {{ t('weather.weather.enableMinWindSpeed') }}
                          </label>
                        </div>

                        <tmx-inline-help
                          [summary]="t('weather.weather.enableMinWindSpeed')"
                          [description]="
                            t(
                              'weather.weather.windSpeedMinInlineHelpDescription'
                            )
                          "
                        ></tmx-inline-help>
                      </div>

                      <label class="form-label" for="minWindSpeed">
                        {{ t('weather.weather.windSpeedMin') }}
                      </label>

                      <div class="px-3">
                        <tmx-slider
                          id="minWindSpeed"
                          [min]="MIN_WIND_SPEED"
                          [max]="MAX_WIND_SPEED"
                          [formControl]="minWindSpeedControl"
                        ></tmx-slider>
                        <tmx-meter
                          [min]="MIN_WIND_SPEED"
                          [max]="MAX_WIND_SPEED"
                          [tickStep]="20"
                        ></tmx-meter>
                      </div>

                      @if (enableMinWindSpeed.getRawValue()) {
                        <div>
                          <div>
                            {{ t('weather.weather.windSpeedMinDescription') }}
                          </div>
                          <strong>{{
                            t('weather.weather.aboveMinWindSpeed', {
                              min: minWindSpeedControl.getRawValue(),
                            })
                          }}</strong>
                        </div>
                      }
                    </div>

                    <div class="config">
                      <div class="d-flex align-items-center gap-2">
                        <div class="form-check form-switch ms-1">
                          <input
                            id="enableMinWindGust"
                            type="checkbox"
                            role="switch"
                            class="form-check-input"
                            [formControlName]="'enableMinWindGust'"
                          />
                          <label
                            for="enableMinWindGust"
                            class="form-check-label"
                          >
                            {{ t('weather.weather.enableMinWindGust') }}
                          </label>
                        </div>

                        <tmx-inline-help
                          [summary]="t('weather.weather.enableMinWindGust')"
                          [description]="
                            t(
                              'weather.weather.windGustMinInlineHelpDescription'
                            )
                          "
                        ></tmx-inline-help>
                      </div>

                      <label class="form-label" for="minWindGust">
                        {{ t('weather.weather.windGustMin') }}
                      </label>

                      <div class="px-3">
                        <tmx-slider
                          id="minWindGust"
                          [min]="MIN_WIND_GUST"
                          [max]="MAX_WIND_GUST"
                          [formControl]="minWindGustControl"
                        ></tmx-slider>
                        <tmx-meter
                          [min]="MIN_WIND_GUST"
                          [max]="MAX_WIND_GUST"
                          [tickStep]="20"
                        ></tmx-meter>
                      </div>

                      @if (enableMinWindGust.getRawValue()) {
                        <div>
                          <div>
                            {{ t('weather.weather.windGustMinDescription') }}
                          </div>
                          <strong>{{
                            t('weather.weather.aboveMinWindGust', {
                              min: minWindGustControl.getRawValue(),
                            })
                          }}</strong>
                        </div>
                      }
                    </div>

                    <div class="config">
                      <div class="d-flex align-items-center gap-2">
                        <div class="form-check form-switch ms-1">
                          <input
                            id="enableTempThreshold"
                            type="checkbox"
                            role="switch"
                            class="form-check-input"
                            [formControlName]="'enableTempThreshold'"
                          />
                          <label
                            for="enableTempThreshold"
                            class="form-check-label"
                          >
                            {{ t('weather.weather.enableTempThreshold') }}
                          </label>
                        </div>

                        <tmx-inline-help
                          [summary]="t('weather.weather.enableTempThreshold')"
                          [description]="
                            t(
                              'weather.weather.tempThresholdInlineHelpDescription'
                            )
                          "
                        ></tmx-inline-help>
                      </div>

                      <label class="form-label" for="temperatureThreshold">
                        {{ t('weather.weather.temperatureThreshold') }}
                      </label>

                      <div class="px-3">
                        <tmx-range-slider
                          id="temperatureThreshold"
                          [min]="MIN_TEMP_THRESHOLD"
                          [max]="MAX_TEMP_THRESHOLD"
                          [formControl]="temperatureThresholdControl"
                        ></tmx-range-slider>
                        <tmx-meter
                          [min]="MIN_TEMP_THRESHOLD"
                          [max]="MAX_TEMP_THRESHOLD"
                          [tickStep]="10"
                        ></tmx-meter>
                      </div>

                      @if (enableTempThresholdControl.getRawValue()) {
                        <div>
                          <div>
                            {{ t('weather.weather.tempThresholdDescription') }}
                          </div>
                          <div>
                            <strong>{{
                              t('weather.weather.minTempDescription', {
                                min: temperatureThresholdControl.getRawValue()
                                  .min,
                              })
                            }}</strong>
                            <span> {{ t('weather.weather.or') }} </span>
                            <strong>{{
                              t('weather.weather.maxTempDescription', {
                                max: temperatureThresholdControl.getRawValue()
                                  .max,
                              })
                            }}</strong>
                          </div>
                        </div>
                      }
                    </div>

                    <div class="config">
                      <div class="d-flex align-items-center gap-2">
                        <div class="form-check form-switch ms-1">
                          <input
                            id="enableTippingIndex"
                            type="checkbox"
                            role="switch"
                            class="form-check-input"
                            [formControlName]="'enableTippingIndex'"
                          />
                          <label
                            for="enableTippingIndex"
                            class="form-check-label"
                          >
                            {{ t('weather.weather.enableTippingIndex') }}
                          </label>
                        </div>

                        <tmx-inline-help
                          [summary]="t('weather.weather.enableTippingIndex')"
                          [description]="
                            t(
                              'weather.weather.tippingIndexThresholdInlineHelpDescription'
                            )
                          "
                        ></tmx-inline-help>
                      </div>

                      <label class="form-label" for="tippingIndexThreshold">
                        {{ t('weather.weather.tippingIndexThreshold') }}
                      </label>

                      <div class="px-3">
                        <div class="d-flex justify-content-between">
                          <span class="h6 text-uppercase text-tertiary">
                            {{ t('weather.weather.noRisk') }}</span
                          >
                          <span class="h6 text-uppercase text-tertiary">{{
                            t('weather.weather.moderate')
                          }}</span>
                          <span class="h6 text-uppercase text-tertiary">
                            {{ t('weather.weather.high') }}
                          </span>
                        </div>
                        <tmx-slider
                          id="tippingIndexThreshold"
                          [min]="0"
                          [max]="tippingIndexOptions().length - 1"
                          [formControl]="tippingIndexThresholdControl"
                        ></tmx-slider>
                        <tmx-meter
                          [min]="0"
                          [max]="tippingIndexOptions().length - 1"
                        ></tmx-meter>
                      </div>

                      @if (enableTippingIndexControl.getRawValue()) {
                        @if (
                          tippingIndexOptions()[
                            tippingIndexThresholdControl.getRawValue()
                          ];
                          as selectedTippingIndex
                        ) {
                          <div>
                            <strong
                              >({{ selectedTippingIndex.value }})
                              {{ selectedTippingIndex.label }}&ensp;</strong
                            >
                            <span>{{ selectedTippingIndex.description }}</span>
                          </div>
                        }
                      }
                    </div>

                    <div class="config">
                      <div class="d-flex align-items-center gap-2">
                        <div class="form-check form-switch ms-1">
                          <input
                            id="enableMinVisibility"
                            type="checkbox"
                            role="switch"
                            class="form-check-input"
                            [formControlName]="'enableMinVisibility'"
                          />
                          <label
                            for="enableMinVisibility"
                            class="form-check-label"
                          >
                            {{ t('weather.weather.enableMinVisibility') }}
                          </label>
                        </div>

                        <tmx-inline-help
                          [summary]="t('weather.weather.enableMinVisibility')"
                          [description]="
                            t(
                              'weather.weather.minVisibilityInlineHelpDescription'
                            )
                          "
                        ></tmx-inline-help>
                      </div>

                      <div class="mb-3">
                        <label class="form-label mb-3" for="minVisibility">
                          {{ t('weather.weather.minVisibility') }}
                        </label>

                        <div class="input-group">
                          <input
                            withValidations
                            decimalInput
                            id="minVisibility"
                            class="form-control"
                            type="number"
                            [formControlName]="'minVisibility'"
                          />
                          <span
                            class="input-group-text bg-secondary bg-opacity-10"
                          >
                            {{ t('weather.weather.mi') }}
                          </span>
                        </div>

                        @if (minVisibilityControl.errors; as errors) {
                          <div class="invalid-feedback d-block">
                            @if (errors['required']) {
                              {{ t('weather.weather.visibilityRequiredError') }}
                            }
                            @if (errors['min']) {
                              {{
                                t('weather.weather.visibilityMinError', {
                                  min: MIN_VISIBILITY,
                                })
                              }}
                            }
                            @if (errors['max']) {
                              {{
                                t('weather.weather.visibilityMaxError', {
                                  max: MAX_VISIBILITY,
                                })
                              }}
                            }
                          </div>
                        }
                      </div>

                      @if (enableMinVisibility.getRawValue()) {
                        <div>
                          <div>
                            {{ t('weather.weather.minVisibilityDescription') }}
                          </div>
                          <strong>{{
                            t('weather.weather.aboveMinVisibility', {
                              min: minVisibilityControl.getRawValue(),
                            })
                          }}</strong>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </form>
            </div>
          }
        }
      </div>
    </div>
  </div>
</ng-container>
